import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { addIncome, fetchIncomeCategories } from "../services/api";
import { X } from "lucide-react";

export default function AddIncomeModal({ onClose, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [idIncomeCategory, setIdIncomeCategory] = useState("");
  const [description, setDescription] = useState("");
  const [categories, setCategories] = useState([]);
  const modalRef = useRef();

  useEffect(() => {
    fetchIncomeCategories().then(setCategories).catch(console.error);
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addIncome({
      income_amt: Number(amount),
      id_income_category: Number(idIncomeCategory),
      income_description: description,
    });
    onSuccess();
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-50 p-4"
      >
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1] // Custom easing for smooth motion
          }}
          className="bg-gray-900 text-white p-6 sm:p-8 rounded-2xl shadow-2xl w-full max-w-md relative"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200"
            aria-label="Close"
          >
            <X size={24} />
          </button>

          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-green-400 font-[Poppins]">
            Add Income
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-medium">
                Amount
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full p-3 border border-gray-700 rounded-lg bg-gray-800 text-white font-[Poppins] 
                           focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent
                           transition-all duration-200"
                required
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 text-sm font-medium">
                Category
              </label>
              <select
                value={idIncomeCategory}
                onChange={(e) => setIdIncomeCategory(e.target.value)}
                className="w-full p-3 border border-gray-700 rounded-lg bg-gray-800 text-white font-[Poppins] 
                           focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent
                           transition-all duration-200"
                required
              >
                <option value="">Select Category</option>
                {Array.isArray(categories) &&
                  categories.map((c) => (
                    <option
                      key={c.id_income_category}
                      value={c.id_income_category}
                    >
                      {c.income_category_name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-300 mb-2 text-sm font-medium">
                Description
              </label>
              <input
                type="text"
                placeholder="Optional description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-3 border border-gray-700 rounded-lg bg-gray-800 text-white font-[Poppins] 
                           focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent
                           transition-all duration-200"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-lg border border-gray-600 text-gray-300 
                           hover:bg-gray-800 hover:border-gray-500 transition-all duration-200 font-[Poppins]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 py-3 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 
                           hover:from-green-600 hover:to-emerald-700 hover:shadow-lg hover:shadow-green-500/30
                           transition-all duration-200 font-[Poppins] font-semibold"
              >
                Add Income
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}